import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { subscriptionDataService, subscriptionUtils } from '../services/subscriptionService.js';
import { usePermissions } from '../hooks/usePermissions';
import { ArrowLeft, Gift, Users, CheckCircle, XCircle, Search, Calendar, Trash2 } from 'lucide-react';

const SubscriptionManagementPage = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeSubscriptions, setEmployeeSubscriptions] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState(null);
  const [error, setError] = useState(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [subscriptionToDeactivate, setSubscriptionToDeactivate] = useState(null);
  const navigate = useNavigate();

  const auth = getAuth();
  const user = auth.currentUser;
  const { hasPermission, loading: permissionsLoading } = usePermissions({ userId: user?.uid });

  // Vérifier si l'utilisateur a la permission de gérer les paiements
  const canManagePayments = hasPermission({ resource: 'payments', action: 'manage' });

  useEffect(() => {
    if (!permissionsLoading && !canManagePayments) {
      navigate('/');
    }
  }, [canManagePayments, permissionsLoading, navigate]);

  useEffect(() => {
    if (canManagePayments) {
      loadEmployees();
    }
  }, [canManagePayments]);

  const loadEmployees = async () => {
    try {
      setLoading(true);

      // Charger tous les employés
      const employeesQuery = query(collection(db, 'employees'));
      const employeesSnapshot = await getDocs(employeesQuery);

      const employeesList = employeesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setEmployees(employeesList);

      // Charger les abonnements pour tous les employés
      const subscriptionsMap = {};
      for (const employee of employeesList) {
        try {
          const subscription = await subscriptionDataService.getCurrentSubscription(db, employee.id);
          subscriptionsMap[employee.id] = subscription;
        } catch (err) {
          console.error(`Erreur lors du chargement de l'abonnement pour ${employee.id}:`, err);
          subscriptionsMap[employee.id] = null;
        }
      }

      setEmployeeSubscriptions(subscriptionsMap);
    } catch (err) {
      console.error('Erreur lors du chargement des employés:', err);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleOfferPlan = async (employeeId) => {
    try {
      setError(null);
      setSuccessMessage(null);

      console.log('🎁 Attribution du plan offert pour:', employeeId);

      // Attribuer le plan offert avec l'ID "offered"
      await subscriptionDataService.assignOfferedPlan(db, employeeId, 'offered');

      setSuccessMessage(`Plan offert attribué avec succès à l'intervenant !`);

      // Recharger les données
      await loadEmployees();

      // Effacer le message après 5 secondes
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('❌ Erreur lors de l\'attribution du plan offert:', err);
      setError(err.message || 'Erreur lors de l\'attribution du plan offert');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleDeactivateClick = (employeeId, subscription) => {
    setSubscriptionToDeactivate({ employeeId, subscription });
    setShowDeactivateModal(true);
  };

  const handleDeactivateConfirm = async () => {
    if (!subscriptionToDeactivate) return;

    try {
      setError(null);
      setSuccessMessage(null);

      console.log('🚫 Désactivation de l\'abonnement:', subscriptionToDeactivate.subscription.id);

      await subscriptionDataService.deactivateSubscription(db, subscriptionToDeactivate.subscription.id);

      setSuccessMessage('Abonnement désactivé avec succès !');

      // Recharger les données
      await loadEmployees();

      // Effacer le message après 5 secondes
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      console.error('❌ Erreur lors de la désactivation:', err);
      setError(err.message || 'Erreur lors de la désactivation de l\'abonnement');
      setTimeout(() => setError(null), 5000);
    } finally {
      setShowDeactivateModal(false);
      setSubscriptionToDeactivate(null);
    }
  };

  const handleDeactivateCancel = () => {
    setShowDeactivateModal(false);
    setSubscriptionToDeactivate(null);
  };

  const getSubscriptionStatus = (subscription) => {
    if (!subscription) {
      return { status: 'none', message: 'Aucun abonnement', color: 'text-gray-500', bgColor: 'bg-gray-100' };
    }

    const isActive = subscriptionUtils.isSubscriptionActive(subscription);
    const isFree = subscriptionUtils.isFreeSubscription(subscription);
    const isOffered = subscriptionUtils.isOfferedSubscription(subscription);

    if (!isActive) {
      return { status: 'expired', message: 'Expiré', color: 'text-red-600', bgColor: 'bg-red-100' };
    }

    if (isOffered) {
      return { status: 'offered', message: 'Offert', color: 'text-purple-600', bgColor: 'bg-purple-100' };
    }

    if (isFree) {
      return { status: 'free', message: 'Gratuit', color: 'text-green-600', bgColor: 'bg-green-100' };
    }

    return { status: 'premium', message: 'Premium', color: 'text-blue-600', bgColor: 'bg-blue-100' };
  };

  const filteredEmployees = employees.filter(employee => {
    const searchLower = searchTerm.toLowerCase();
    return (
      employee.firstName?.toLowerCase().includes(searchLower) ||
      employee.lastName?.toLowerCase().includes(searchLower) ||
      employee.email?.toLowerCase().includes(searchLower)
    );
  });

  if (permissionsLoading || loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!canManagePayments) {
    return null;
  }

  return (
    <div className="w-full bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto p-4">
        {/* En-tête */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft size={24} className="text-gray-700 dark:text-gray-300" />
          </button>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <Gift className="mr-2" />
            Gestion des abonnements
          </h1>
          <div className="w-10"></div>
        </div>

        {/* Barre de recherche */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Rechercher un intervenant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-800 dark:text-white"
            />
          </div>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total intervenants</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{employees.length}</p>
              </div>
              <Users className="text-gray-400" size={32} />
            </div>
          </div>

          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 dark:text-purple-400">Plans offerts</p>
                <p className="text-2xl font-bold text-purple-900 dark:text-purple-300">
                  {Object.values(employeeSubscriptions).filter(s => s && subscriptionUtils.isOfferedSubscription(s)).length}
                </p>
              </div>
              <Gift className="text-purple-400" size={32} />
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 dark:text-blue-400">Abonnements premium</p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-300">
                  {Object.values(employeeSubscriptions).filter(s =>
                    s && subscriptionUtils.isSubscriptionActive(s) && s.status === 'active'
                  ).length}
                </p>
              </div>
              <CheckCircle className="text-blue-400" size={32} />
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Sans abonnement</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Object.values(employeeSubscriptions).filter(s => !s || !subscriptionUtils.isSubscriptionActive(s)).length}
                </p>
              </div>
              <XCircle className="text-gray-400" size={32} />
            </div>
          </div>
        </div>

        {/* Liste des employés */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Intervenant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Fin d'abonnement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                {filteredEmployees.map((employee) => {
                  const subscription = employeeSubscriptions[employee.id];
                  const status = getSubscriptionStatus(subscription);

                  return (
                    <tr key={employee.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-medium">
                              {employee.firstName?.[0]}{employee.lastName?.[0]}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {employee.firstName} {employee.lastName}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-300">{employee.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${status.bgColor} ${status.color}`}>
                          {status.message}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {subscription && subscription.currentPeriodEnd ? (
                          <div className="flex items-center">
                            <Calendar size={14} className="mr-1" />
                            {new Date(subscription.currentPeriodEnd).toLocaleDateString('fr-FR')}
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          {status.status !== 'offered' && status.status !== 'none' && (
                            <button
                              onClick={() => handleDeactivateClick(employee.id, subscription)}
                              className="inline-flex items-center px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                              title="Désactiver l'abonnement (test)"
                            >
                              <Trash2 size={14} className="mr-1" />
                              Désactiver
                            </button>
                          )}
                          {status.status !== 'offered' && status.status === 'none' && (
                            <button
                              onClick={() => handleOfferPlan(employee.id)}
                              className="inline-flex items-center px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-md transition-colors"
                            >
                              <Gift size={14} className="mr-1" />
                              Offrir premium
                            </button>
                          )}
                          {status.status === 'offered' && (
                            <>
                              <span className="text-purple-600 dark:text-purple-400 font-medium">
                                ✓ Plan offert actif
                              </span>
                              <button
                                onClick={() => handleDeactivateClick(employee.id, subscription)}
                                className="inline-flex items-center px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                                title="Désactiver l'abonnement (test)"
                              >
                                <Trash2 size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredEmployees.length === 0 && (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Aucun intervenant trouvé</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {searchTerm ? 'Essayez avec un autre terme de recherche' : 'Aucun intervenant disponible'}
              </p>
            </div>
          )}
        </div>

        {/* Messages de succès */}
        {successMessage && (
          <div className="fixed bottom-4 right-4 bg-green-100 dark:bg-green-900 border-l-4 border-green-500 dark:border-green-400 text-green-700 dark:text-green-300 p-4 rounded shadow-lg max-w-md z-50">
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <span>{successMessage}</span>
            </div>
          </div>
        )}

        {/* Messages d'erreur */}
        {error && (
          <div className="fixed bottom-4 right-4 bg-red-100 dark:bg-red-900 border-l-4 border-red-500 dark:border-red-400 text-red-700 dark:text-red-300 p-4 rounded shadow-lg max-w-md z-50">
            <div className="flex items-start">
              <XCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Modal de confirmation de désactivation */}
        {showDeactivateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <div className="flex items-center mb-4">
                <div className="bg-red-100 dark:bg-red-900/20 rounded-full p-3 mr-3">
                  <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Désactiver l'abonnement
                </h3>
              </div>

              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Êtes-vous sûr de vouloir désactiver cet abonnement ? Cette action est utilisée pour les tests et mettra l'abonnement en statut "annulé".
              </p>

              {subscriptionToDeactivate && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-6">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Type d'abonnement</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {subscriptionToDeactivate.subscription.status === 'free'
                      ? 'Gratuit'
                      : subscriptionToDeactivate.subscription.status === 'offered'
                      ? 'Offert (Premium)'
                      : 'Premium'}
                  </p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={handleDeactivateCancel}
                  className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-white rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDeactivateConfirm}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Désactiver
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionManagementPage;
